import { Checkbox, Form, FormProps, message, Select, Upload } from 'antd-v5';
import { RcFile } from 'antd-v5/es/upload';
import { useEffect, useState } from 'react';
import RecallInput from '../../widget/RecallInput';
import RecallSelect from '../../widget/RecallSelect';
import RecallModal from '../RecallModal';
import useUpload from './useUpload';
import CanCloseImage from './CanCloseImage';
import css from './index.module.scss';
import { useLang } from '@/app/utils/useLang';
import { loginUser } from '@/utils/cookie';
import request from '@/utils/request';
import { baseUrl } from '@/service/util';
import getLocal from '@/app/utils/i18n';
import { setLocaleQuery } from '@/utils';
import { getInterCountriesList } from '@/service';

type FieldType = {
    name: string;
    phone: string;
    email: string;
    // 邮编
    zipCode: string;
    // 地址
    address: string;
    // 国家
    country: string;
    // 省份
    state: string;
    // 市区/村
    city: string;
    // 是否同意协议
    agreed: boolean;
};

const ErrorMessage = ({ text }: { text: string }) => (
    <div className="B-error-message">
        <img src="https://websiteoss.ecoflow.com/upload/image/1739345027002/info.png" alt="" />
        <span>{text}</span>
    </div>
);

const B = ({ snCode, callback }: { snCode: string; callback: (isSuccess: boolean) => void }) => {
    const { locale } = getLocal();
    const { t } = useLang('recall.B');

    // 省份列表
    const [provinceList, setProvinceList] = useState<{ id: number; label: string }[]>([]);
    useEffect(() => {
        getInterCountriesList('JP').then((res) => {
            if (res.code === '0') {
                setProvinceList(res.data);
            }
        });
    }, []);

    // 上传图片
    const { upload } = useUpload();
    const [imgFile, setImgFile] = useState<RcFile | undefined>(undefined);
    const [imgUrl, setImgUrl] = useState('');
    const customUpload = async ({ file }: any) => {
        // 不是jpg、jpeg、png格式
        if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
            message.open({
                type: 'error',
                content: '画像形式：jpg、jpeg、png',
                style: {
                    transform: 'translateY(200%)',
                },
            });
            form.setFieldsValue({ file: imgFile });
            form.validateFields(['file']);
            return;
        }
        // 大于5MB
        if (file.size > 5 * 1024 * 1024) {
            message.open({
                type: 'error',
                content: '画像サイズは5MBまで',
                style: {
                    transform: 'translateY(200%)',
                },
            });
            form.setFieldsValue({ file: imgFile });
            form.validateFields(['file']);
            return;
        }
        setImgFile(file);
        const url = URL.createObjectURL(file);
        setImgUrl(url);
    };

    // 表单上传
    const [showModal, setShowModal] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [form] = Form.useForm();
    const [formData, setFormData] = useState<FieldType>();
    const onConfirm = async () => {
        setModalLoading(true);
        if (!imgFile || !formData) {
            setModalLoading(false);
            setShowModal(false);
            return;
        }
        try {
            const img = await upload(imgFile);
            request
                .post(`${baseUrl(locale)}/tradeIn/recall/form/submit${setLocaleQuery(locale)}`, {
                    data: {
                        name: formData.name,
                        // 手机号去掉 -
                        phone: formData.phone.replace(/-/g, ''),
                        email: loginUser().user.email,
                        address: formData.address,
                        state: formData.state,
                        city: formData.city,
                        zipCode: formData.zipCode,
                        agreed: formData.agreed,
                        sn: snCode,
                        snUrl: img,
                    },
                    needToken: true,
                })
                .then((res) => {
                    if (res.code === '0') {
                        callback(true);
                    }
                    setModalLoading(false);
                    setShowModal(false);
                    setTimeout(() => {
                        document.body.scrollTop = 0;
                        document.documentElement.scrollTop = 0;
                        window.scrollTo({ behavior: 'auto', top: 0 });
                    }, 100);
                });
        } catch (err) {
            setModalLoading(false);
            setShowModal(false);
        }
    };
    const onFinish: FormProps<FieldType>['onFinish'] = async (values) => {
        setFormData(values);
        setShowModal(true);
    };

    return (
        <div className={`${css.page}`}>
            {/* 表单 */}
            <Form className="B-form" form={form} layout="vertical" onFinish={onFinish}>
                <Form.Item label={t('name')} name="name" rules={[{ required: true, message: <ErrorMessage text={t('required')} /> }]}>
                    <RecallInput maxLength={100} />
                </Form.Item>
                <Form.Item label={t('phone')} name="phone" rules={[{ required: true, message: <ErrorMessage text={t('required')} /> }]}>
                    <RecallInput />
                </Form.Item>
                <Form.Item label={t('email')} name="email" required>
                    <RecallInput readOnly defaultValue={loginUser()?.user?.email} />
                </Form.Item>
                <Form.Item label={t('code')} name="zipCode" rules={[{ required: true, message: <ErrorMessage text={t('required')} /> }]}>
                    <RecallInput />
                </Form.Item>
                <Form.Item label={t('country')} name="country" required>
                    <RecallSelect defaultValue="日本" disabled>
                        <Select.Option value="日本">日本</Select.Option>
                    </RecallSelect>
                </Form.Item>
                <Form.Item label={t('state')} name="state" rules={[{ required: true, message: <ErrorMessage text={t('required')} /> }]}>
                    <RecallSelect optionFilterProp="children" showSearch>
                        {provinceList.map((item) => (
                            <Select.Option value={item.label} key={item.id}>
                                {item.label}
                            </Select.Option>
                        ))}
                    </RecallSelect>
                </Form.Item>
                <Form.Item
                    className="B-form-item-col2"
                    label={t('city')}
                    name="city"
                    rules={[{ required: true, message: <ErrorMessage text={t('required')} /> }]}
                >
                    <RecallInput />
                </Form.Item>

                <Form.Item
                    label={t('address')}
                    className="B-form-item-col2"
                    name="address"
                    rules={[{ required: true, message: <ErrorMessage text={t('required')} /> }]}
                >
                    <RecallInput maxLength={300} />
                </Form.Item>
                <Form.Item
                    label={t('upload')}
                    name="file"
                    className="B-form-item-col2"
                    rules={[{ required: true, message: <ErrorMessage text={t('required')} /> }]}
                >
                    <div className="B-form-item-upload-content">
                        <Upload
                            className="B-form-item-upload"
                            accept="image/jpeg,image/jpg,image/png"
                            maxCount={1} // 限制只能上传一个文件
                            style={{ width: '100%' }}
                            showUploadList={false}
                            beforeUpload={() => false} // 阻止默认上传
                            onChange={customUpload}
                        >
                            <button className="B-form-item-upload-btn btn-ani" type="button">
                                <img src="https://websiteoss.ecoflow.com/upload/image/1739342045511/export.png" alt="" />
                                {t('upload_btn')}
                            </button>
                        </Upload>
                        <CanCloseImage
                            url={imgUrl}
                            onClose={() => {
                                setImgFile(undefined);
                                setImgUrl('');
                                form.setFieldsValue({ file: undefined });
                                form.validateFields(['file']);
                            }}
                        />
                        <div className="B-form-item-upload-desc">{t('img_format')}</div>
                    </div>
                </Form.Item>
                <Form.Item
                    className="B-form-item-col2 B-form-item-checkbox-group"
                    name="agreed"
                    rules={[{ required: true, message: <ErrorMessage text={t('rule_err')} /> }]}
                >
                    <Checkbox
                        className="B-form-item-checkbox"
                        onChange={(e) => {
                            form.setFieldsValue({ agreed: e.target.checked ? true : undefined });
                            form.validateFields(['agreed']);
                        }}
                    >
                        <span className="checkbox-text">
                            {t.rich('rule', {
                                l2: (v: string) => (
                                    <a
                                        className="bottom-line"
                                        href="https://account.ecoflow.com/agreement/ja-jp/PrivacyPolicy.html"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {v}
                                    </a>
                                ),
                                l: (v: string) => (
                                    <a
                                        className="bottom-line"
                                        href="https://account.ecoflow.com/agreement/ja-jp/TermsOfUse.html"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {v}
                                    </a>
                                ),
                            })}
                        </span>
                    </Checkbox>
                </Form.Item>
                <Form.Item className="B-form-item-col2">
                    <div style={{ height: '1px', backgroundColor: '#E6E6E6' }}></div>
                </Form.Item>
                <Form.Item className="B-form-item-col2">
                    <button className="B-form-item-submit btn-ani submit" type="submit">
                        {t('submit')}
                    </button>
                </Form.Item>
            </Form>

            {/* 确认提交 */}
            <RecallModal open={showModal} code={snCode} loading={modalLoading} onCancel={() => setShowModal(false)} onConfirm={onConfirm} />
        </div>
    );
};
export default B;
